/**
 * Constants for Discussion feature (Socratic dialogue)
 *
 * Shared between frontend and backend to ensure consistent tone and reading level guidance.
 */

// ============================================================================
// AI Tutor Name
// ============================================================================

/**
 * Name of the AI conversation partner in discussions.
 */
export const AI_TUTOR_NAME = 'Sage'

// ============================================================================
// Reading Levels
// ============================================================================

export const DISCUSSION_READING_LEVELS = ['middle_school', 'high_school', 'college', 'phd'] as const

export type ReadingLevel = (typeof DISCUSSION_READING_LEVELS)[number]

// ============================================================================
// Tone Levels
// ============================================================================

export const DISCUSSION_TONE_LEVELS = ['formal', 'balanced', 'casual'] as const

export type ToneLevel = (typeof DISCUSSION_TONE_LEVELS)[number]

// ============================================================================
// Expression Moods
// ============================================================================

export const EXPRESSION_MOODS = [
  'curious',
  'excited',
  'elated',
  'thinking',
  'surprised',
  'encouraging',
  'playful',
  'riveted',
  'neutral'
] as const

export type ExpressionMood = (typeof EXPRESSION_MOODS)[number]

export const EXPRESSION_MOOD_GUIDANCE: Record<ExpressionMood, string> = {
  curious: 'Asking questions, exploring concepts - thoughtful and questioning',
  excited: 'Big revelations, enthusiastic teaching - energetic and animated',
  elated: 'Deep satisfaction, proud moments - joyful and content',
  thinking: 'Processing information, working through problems - contemplative',
  surprised: '"Aha!" moments, unexpected connections - wide-eyed amazement',
  encouraging: 'Providing support, positive reinforcement - warm and supportive',
  playful: 'Fun facts, lighthearted moments - mischievous and fun',
  riveted: 'Captivated by developments, "what happens next" suspense - intensely focused',
  neutral: 'Default state, steady explanation - calm and balanced'
}

export const DEFAULT_EXPRESSION_MOOD: ExpressionMood = 'neutral'

// Token limits for discussion content (matching learn system)
// Claude Sonnet has 200k context window for Sonnet 4.5, we leave headroom for prompts and response
// Haiku 4.5 has 200k context window as well
export const DISCUSSION_MAX_CONTENT_TOKENS = 150_000

// Character limit for note content (5 chars per token as conservative upper bound)
export const DISCUSSION_MAX_CONTENT_CHARS = DISCUSSION_MAX_CONTENT_TOKENS * 5
